import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Eye, EyeOff, Upload } from 'lucide-react';
import { compressImage } from '@/lib/imageCompression';
import logo from '../assets/logo.webp';

export default function AuthPage({ auth }) {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureDate, setProfilePictureDate] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    surname: '',
    location: '',
    gender: '',
    is_over_13: false,
    agreed_to_terms: false
  });
  const [loading, setLoading] = useState(false);

  const showErrorDetails = (error) => {
    const debugInfo = {
      message: error.message,
      name: error.name,
      stack: error.stack,
      responseData: error.response?.data,
      responseStatus: error.response?.status,
      configUrl: error.config?.url,
      configMethod: error.config?.method,
      configData: error.config?.data,
    };
    console.error('[Auth Error Debug Info]', debugInfo);
    alert(`Error Debug Info:\n\n${JSON.stringify(debugInfo, null, 2)}`);
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setLoading(true); // Show loading while compressing
      try {
        const { base64, dateTaken } = await compressImage(file);
        setProfilePicture(base64);
        setProfilePictureDate(dateTaken);
        setProfilePicturePreview(base64);
      } catch (err) {
        toast.error("Failed to process image");
      } finally {
         setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (isForgotPassword) {
      try {
        await axios.post(`${API}/auth/request-reset`, { email: formData.email });
        toast.success("If that email exists, a reset link has been sent.");
        setIsForgotPassword(false);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'An error occurred', {
          action: {
            label: 'View more',
            onClick: () => showErrorDetails(error)
          }
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';

      // Verify age checkbox
      if (!isLogin) {
          if (!formData.is_over_13) {
              toast.error("You must be at least 13 years old to sign up.");
              setLoading(false);
              return;
          }
      }

      // Filter out is_over_13 and agreed_to_terms from payload as backend doesn't expect it
      const { is_over_13, agreed_to_terms, ...rest } = formData;
      const payload = isLogin ? rest : {
        ...rest,
        profile_picture: profilePicture,
        profile_picture_date: profilePictureDate
      };
      const response = await axios.post(`${API}${endpoint}`, payload);

      auth.login(response.data.access_token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred', {
        action: {
          label: 'View more',
          onClick: () => showErrorDetails(error)
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setIsForgotPassword(false);
    setProfilePicture(null);
    setProfilePictureDate(null);
    setProfilePicturePreview(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 glass" data-testid="auth-card">
        <div className="text-center mb-8">
          <img src={logo} alt="12 Challenges Logo" className="mx-auto h-24 mb-4" />
          <p className="text-gray-600" data-testid="auth-subtitle">
            {isForgotPassword ? 'Reset Password' : (isLogin ? 'Welcome back!' : 'Join the community')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
          {!isLogin && (
            <div className="flex justify-center mb-4">
              <div className="relative">
                <Avatar className="w-24 h-24">
                  {profilePicturePreview ? (
                    <img src={profilePicturePreview} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <AvatarFallback className="text-2xl">+</AvatarFallback>
                  )}
                </Avatar>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                  className="hidden"
                  id="profile-picture-upload"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="absolute bottom-0 right-0 rounded-full p-2"
                  onClick={() => document.getElementById('profile-picture-upload').click()}
                  data-testid="upload-profile-picture-button"
                >
                  <Upload size={16} />
                </Button>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="auth-email-input"
              className="mt-1"
            />
          </div>

          {!isForgotPassword && (
            <div>
              <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="auth-password-input"
                className="mt-1 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {isLogin && (
              <div className="text-right mt-1">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-sm text-teal-600 hover:text-teal-700"
                >
                  Forgot Password?
                </button>
              </div>
            )}
          </div>
          )}

          {!isLogin && !isForgotPassword && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">First Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="auth-name-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="surname">Last Name</Label>
                  <Input
                    id="surname"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    required
                    data-testid="auth-surname-input"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  data-testid="auth-location-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="gender">Gender</Label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  data-testid="auth-gender-select"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>

              <div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_over_13"
                  name="is_over_13"
                  checked={formData.is_over_13}
                  onChange={handleChange}
                  className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  required
                />
                <Label htmlFor="is_over_13" className="text-sm text-gray-600 font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  I confirm that I am over 13 years old
                </Label>
              </div>
              </div>

              <div>
              <div className="flex items-start space-x-2">
                 <input
                   type="checkbox"
                   id="agreed_to_terms"
                   name="agreed_to_terms"
                   checked={formData.agreed_to_terms}
                   onChange={handleChange}
                   className="h-4 w-4 mt-0.5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                   required
                 />
                 <Label htmlFor="agreed_to_terms" className="text-sm leading-tight text-gray-600">
                   I agree to the{' '}
                   <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">
                     Terms of Service
                   </a>{' '}
                   and{' '}
                   <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">
                     Privacy Policy
                   </a>
                 </Label>
              </div>
              </div>
            </>
          )}

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white"
            disabled={loading}
            data-testid="auth-submit-button"
          >
            {loading ? 'Please wait...' : (isForgotPassword ? 'Send Reset Link' : (isLogin ? 'Sign In' : 'Sign Up'))}
          </Button>
        </form>

        <div className="mt-6 text-center">
          {isForgotPassword ? (
            <button
              onClick={() => setIsForgotPassword(false)}
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              Back to Sign In
            </button>
          ) : (
            <button
              onClick={toggleMode}
              className="text-teal-600 hover:text-teal-700 font-medium"
              data-testid="auth-toggle-button"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}